import { c as _c } from "react/compiler-runtime";
import type { KeyboardEventHandler, PointerEvent, PropsWithChildren, ReactNode } from 'react';
import { useCachedValue } from '@morpho-org/dapp-core';
import { cn, Icon, useHasTouchInput } from '@repo/ui';
import { ArrowUpToLine, Points, Rewards, WarningTriangleSolid } from '@repo/ui/icons';
import { QueryClientContext } from '@tanstack/react-query';
import { useContext, useEffect, useId, useRef, useState } from 'react';
import { HoverCard, HoverCardContent, HoverCardTrigger, Popover, PopoverContent, PopoverTrigger } from '@/components/common/overlays';
import { mapVaultApyCardRows, mapVaultRowApyCardRows } from '@/helpers/vaultApyCardRows';
import { prefetchVaultV2ApyQuery, type VaultV2ApyQueryInput } from '@/hooks/useVaultV2ApyQuery';
import type { RewardsHoverCardProps } from '../RewardsHoverCard';
import MerklRewardsHoverCard, { type MerklTarget } from '../RewardsHoverCard/MerklRewardsHoverCard';
const hasEntries = <T,>(_prev: readonly T[] | null | undefined, next: readonly T[] | null | undefined) => !!next?.length;
export interface IconRewardHoverProps extends PropsWithChildren {
  RewardsHoverCardProps: RewardsHoverCardProps;
  merklContext?: MerklTarget | null;
  noHover?: boolean;
  iconSize?: number;
  isReversed?: boolean;
  isMobileSupportEnabled?: boolean;
  vaultV2ApyInput?: VaultV2ApyQueryInput;
  /** A list or dashboard row: without a breakdown, it keeps the allowlist sparkle. */
  isListRow?: boolean;
  /**
   * Id of an element that names the trigger. When set, the trigger renders as a
   * focusable button whose accessible name combines that element's text with its
   * own, and the card becomes a popover (a hover card can't hold focusable
   * links): Enter/Space opens it, links inside are tabbable, and Escape closes
   * it and returns focus to the trigger.
   */
  labelledBy?: string;
}
const getVaultApyRows = (props: RewardsHoverCardProps, isVaultV2: boolean, isListRow: boolean): ReturnType<typeof mapVaultApyCardRows> | null => {
  if (!isVaultV2 || props.apyBreakdown === undefined) return null;
  if (isListRow) {
    return mapVaultRowApyCardRows({
      breakdown: props.apyBreakdown ?? null,
      apy: props.apy,
      netApy: props.netApy,
      rewards: props.rewards,
      includesDonations: props.includesDonations,
      isRateCapped: props.isRateCapped,
      maxApy: props.maxApy
    });
  }
  return mapVaultApyCardRows({
    breakdown: props.apyBreakdown ?? null,
    netApyExcludingRewards: props.apy == null ? null : Number(props.apy) / 1e18,
    rewards: (props.rewards ?? []).map(reward => Number(reward.apr ?? 0n) / 1e18),
    includesDonations: props.includesDonations ?? false,
    isRateCapped: props.isRateCapped ?? false,
    maxApy: props.maxApy ?? null
  });
};
const getIconState = ({
  isVaultV2,
  hasRewards,
  hasPoints,
  includesDonations,
  isRateCapped,
  maxApy
}: {
  isVaultV2: boolean;
  hasRewards: boolean;
  hasPoints: boolean;
  includesDonations?: boolean;
  isRateCapped?: boolean;
  maxApy?: number | null;
}) => {
  const hasRewardsSparkle = hasRewards || !!includesDonations;
  const hasPausedIcon = maxApy === 0;
  const hasCapIcon = !!isRateCapped && !hasPausedIcon;
  const hasCard = isVaultV2 ? hasRewardsSparkle || hasCapIcon || hasPausedIcon || hasPoints : true;
  return {
    hasRewardsSparkle,
    hasCapIcon,
    hasPausedIcon,
    hasCard
  };
};
const getTriggerContent = ({
  children,
  iconSize,
  isReversed,
  isInteractive,
  hasRewardsSparkle,
  hasCapIcon,
  hasPausedIcon,
  isVaultV2,
  hasPoints,
  onKeyDown,
  onPointerEnter,
  onFocus,
  labelledBy,
  triggerId
}: {
  children: ReactNode;
  iconSize?: number;
  isReversed: boolean;
  isInteractive: boolean;
  hasRewardsSparkle: boolean;
  hasCapIcon: boolean;
  hasPausedIcon: boolean;
  isVaultV2: boolean;
  hasPoints: boolean;
  onKeyDown: KeyboardEventHandler<HTMLDivElement>;
  onPointerEnter?: () => void;
  onFocus?: () => void;
  labelledBy?: string;
  triggerId?: string;
}) => {
  const className = cn('flex items-center gap-[2px]', isReversed ? 'flex-row-reverse' : 'flex-row', isInteractive && "relative after:absolute after:inset-x-0 after:-inset-y-1 after:content-['']");
  const content = <>
      {children}
      {hasRewardsSparkle && <Icon className="text-info-foreground" data-testid="icon-reward-hover-icon" icon={<Rewards data-testid="icon-reward-hover-sparkle" />} style={iconSize ? {
      width: iconSize,
      height: iconSize
    } : undefined} />}
      {hasCapIcon && isVaultV2 && <Icon className="text-info-foreground" data-testid="icon-reward-hover-icon" icon={<ArrowUpToLine />} style={iconSize ? {
      width: iconSize,
      height: iconSize
    } : undefined} />}
      {hasPausedIcon && isVaultV2 && <Icon className="text-warning-foreground" data-testid="icon-reward-hover-icon" icon={<WarningTriangleSolid />} style={iconSize ? {
      width: iconSize,
      height: iconSize
    } : undefined} />}
      {hasPoints && <Icon className="text-info-foreground" data-testid="icon-reward-hover-icon" icon={<Points />} style={iconSize ? {
      width: iconSize,
      height: iconSize
    } : undefined} />}
    </>;
  if (labelledBy && isInteractive) {
    return <button type="button" id={triggerId} aria-labelledby={`${labelledBy} ${triggerId}`} className={cn(className, 'rounded-md focus-visible:ring-ring/50 focus-visible:ring-[3px] focus-visible:outline-none')} data-testid="icon-reward-hover-trigger" onPointerEnter={onPointerEnter} onFocus={onFocus}>
        {content}
      </button>;
  }
  return <div className={className} data-testid="icon-reward-hover-trigger" role={isInteractive ? 'button' : undefined} tabIndex={isInteractive ? 0 : -1} onKeyDown={onKeyDown} onPointerEnter={onPointerEnter} onFocus={onFocus}>
      {content}
    </div>;
};

/**
 * The card as a popover named by `labelledBy`: it opens on mouse hover, click and
 * Enter/Space, holds focusable links, and Escape returns focus to the trigger.
 */
const LabelledCardPopover = t0 => {
  const $ = _c(25);
  const {
    isOpen,
    setIsOpen,
    labelledBy,
    children: t1
  } = t0;
  const [trigger, content] = t1;
  const openRef = useRef(false);
  const contentRef = useRef(null);
  const openMethod = useRef("pointer");
  const pendingMethod = useRef("pointer");
  const hoverTimer = useRef(undefined);
  let t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => () => clearTimeout(hoverTimer.current);
    t3 = [];
    $[0] = t2;
    $[1] = t3;
  } else {
    t2 = $[0];
    t3 = $[1];
  }
  useEffect(t2, t3);
  let t4;
  if ($[2] !== setIsOpen) {
    t4 = (next, method) => {
      if (openRef.current === next) {
        return;
      }
      openRef.current = next;
      if (next && method) {
        openMethod.current = method;
      }
      setIsOpen(next);
    };
    $[2] = setIsOpen;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const setOpen = t4;
  let t5;
  if ($[4] !== setOpen) {
    t5 = next_0 => {
      clearTimeout(hoverTimer.current);
      hoverTimer.current = setTimeout(() => setOpen(next_0, "hover"), next_0 ? 200 : 100);
    };
    $[4] = setOpen;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const scheduleHover = t5;
  let t6;
  if ($[6] !== scheduleHover) {
    t6 = {
      onPointerEnter: e => {
        if (e.pointerType !== "mouse") {
          return;
        }
        scheduleHover(true);
      },
      onPointerLeave: e_0 => {
        if (e_0.pointerType !== "mouse") {
          return;
        }
        if (openRef.current && openMethod.current !== "hover") {
          clearTimeout(hoverTimer.current);
          return;
        }
        scheduleHover(false);
      }
    };
    $[6] = scheduleHover;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const hoverProps = t6;
  let t7;
  if ($[8] !== setOpen) {
    t7 = next_1 => {
      clearTimeout(hoverTimer.current);
      setOpen(next_1, pendingMethod.current);
    };
    $[8] = setOpen;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = e_1 => {
      if (openRef.current && openMethod.current === "hover") {
        e_1.preventDefault();
        clearTimeout(hoverTimer.current);
        if (e_1.detail === 0) {
          openMethod.current = "keyboard";
          contentRef.current?.focus();
        } else {
          openMethod.current = "pointer";
        }
        return;
      }
      pendingMethod.current = e_1.detail === 0 ? "keyboard" : "pointer";
    };
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== hoverProps || $[12] !== trigger) {
    t9 = <PopoverTrigger asChild={true} {...hoverProps} onClick={t8}>{trigger}</PopoverTrigger>;
    $[11] = hoverProps;
    $[12] = trigger;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  let t11;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = e_3 => {
      e_3.preventDefault();
      if (openMethod.current === "keyboard") {
        contentRef.current?.focus();
      }
    };
    t11 = e_4 => {
      if (openMethod.current === "hover") {
        e_4.preventDefault();
      }
    };
    $[14] = t10;
    $[15] = t11;
  } else {
    t10 = $[14];
    t11 = $[15];
  }
  let t12;
  if ($[16] !== content || $[17] !== hoverProps || $[18] !== labelledBy) {
    t12 = <PopoverContent {...hoverProps} ref={contentRef} tabIndex={-1} side="top" sideOffset={8} className="rounded-[20px] p-0 outline-none" aria-labelledby={labelledBy} onClick={_temp} onOpenAutoFocus={t10} onCloseAutoFocus={t11}>{content}</PopoverContent>;
    $[16] = content;
    $[17] = hoverProps;
    $[18] = labelledBy;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] !== isOpen || $[21] !== t12 || $[22] !== t7 || $[23] !== t9) {
    t13 = <div className="w-fit"><Popover open={isOpen} onOpenChange={t7}>{t9}{t12}</Popover></div>;
    $[20] = isOpen;
    $[21] = t12;
    $[22] = t7;
    $[23] = t9;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  return t13;
};
const resolveIconState = (rows: ReturnType<typeof getVaultApyRows>, iconState: ReturnType<typeof getIconState>, hasPoints: boolean) => ({
  hasRewardsSparkle: rows?.hasSparkle ?? iconState.hasRewardsSparkle,
  hasCapIcon: rows?.hasCapIcon ?? iconState.hasCapIcon,
  hasPausedIcon: rows?.hasPausedIcon ?? iconState.hasPausedIcon,
  hasCard: (rows?.hasCard ?? iconState.hasCard) || hasPoints
});
const IconRewardHover = t0 => {
  const $ = _c(33);
  const {
    iconSize,
    RewardsHoverCardProps,
    merklContext,
    children,
    noHover,
    isReversed: t1,
    isMobileSupportEnabled,
    vaultV2ApyInput,
    isListRow: t2,
    labelledBy
  } = t0;
  const isReversed = t1 === undefined ? false : t1;
  const isListRow = t2 === undefined ? false : t2;
  const [isOpen, setIsOpen] = useState(false);
  const hasTouchInput = useHasTouchInput();
  const queryClient = useContext(QueryClientContext);
  const {
    points,
    rewards,
    includesDonations
  } = RewardsHoverCardProps;
  const cachedRewards = useCachedValue(rewards, hasEntries);
  const cachedPoints = useCachedValue(points, hasEntries);
  const isVaultV2 = RewardsHoverCardProps.apyBreakdown !== undefined || vaultV2ApyInput !== undefined;
  const rows = getVaultApyRows(RewardsHoverCardProps, isVaultV2, isListRow);
  const iconState = getIconState({
    isVaultV2,
    hasRewards: !!cachedRewards?.length,
    hasPoints: !!cachedPoints?.length,
    includesDonations,
    isRateCapped: RewardsHoverCardProps.isRateCapped,
    maxApy: RewardsHoverCardProps.maxApy
  });
  const {
    hasRewardsSparkle,
    hasCapIcon,
    hasPausedIcon,
    hasCard
  } = resolveIconState(rows, iconState, !!cachedPoints?.length);
  const isInteractive = !noHover && hasCard;
  const isTouchPopover = hasTouchInput && !!isMobileSupportEnabled;
  const triggerId = useId();
  let t3;
  if ($[0] !== queryClient || $[1] !== vaultV2ApyInput) {
    t3 = () => {
      if (queryClient && vaultV2ApyInput) {
        prefetchVaultV2ApyQuery(queryClient, vaultV2ApyInput);
      }
    };
    $[0] = queryClient;
    $[1] = vaultV2ApyInput;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const prefetchApy = t3;
  const handleKeyDown = event => {
    if (!isInteractive) {
      return;
    }
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      if (isTouchPopover) {
        setIsOpen(_temp2);
      } else {
        setIsOpen(true);
      }
    } else {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    }
  };
  const triggerContent = getTriggerContent({
    children,
    iconSize,
    isReversed,
    isInteractive,
    hasRewardsSparkle,
    hasCapIcon,
    hasPausedIcon,
    isVaultV2,
    hasPoints: !!cachedPoints?.length,
    onKeyDown: handleKeyDown,
    onPointerEnter: hasCard ? prefetchApy : undefined,
    onFocus: hasCard ? prefetchApy : undefined,
    labelledBy,
    triggerId
  });
  let t4;
  if ($[3] !== RewardsHoverCardProps || $[4] !== merklContext || $[5] !== vaultV2ApyInput) {
    t4 = <MerklRewardsHoverCard {...RewardsHoverCardProps} merklContext={merklContext} vaultV2ApyInput={vaultV2ApyInput} />;
    $[3] = RewardsHoverCardProps;
    $[4] = merklContext;
    $[5] = vaultV2ApyInput;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const hoverCardContent = t4;
  if (!hasCard) {
    let t5;
    if ($[7] !== triggerContent) {
      t5 = <div className="w-fit">{triggerContent}</div>;
      $[7] = triggerContent;
      $[8] = t5;
    } else {
      t5 = $[8];
    }
    return t5;
  }
  if (labelledBy && isInteractive) {
    let t5;
    if ($[9] !== hoverCardContent || $[10] !== isOpen || $[11] !== labelledBy || $[12] !== triggerContent) {
      t5 = <LabelledCardPopover isOpen={isOpen} setIsOpen={setIsOpen} labelledBy={labelledBy}>{triggerContent}{hoverCardContent}</LabelledCardPopover>;
      $[9] = hoverCardContent;
      $[10] = isOpen;
      $[11] = labelledBy;
      $[12] = triggerContent;
      $[13] = t5;
    } else {
      t5 = $[13];
    }
    return t5;
  }
  if (isTouchPopover) {
    let t5;
    if ($[14] !== noHover) {
      t5 = e => {
        if (noHover) {
          return;
        }
        e.preventDefault();
        e.stopPropagation();
        setIsOpen(_temp3);
      };
      $[14] = noHover;
      $[15] = t5;
    } else {
      t5 = $[15];
    }
    let t6;
    if ($[16] !== noHover || $[17] !== t5 || $[18] !== triggerContent) {
      t6 = <PopoverTrigger asChild={true} disabled={noHover} onClick={t5}>{triggerContent}</PopoverTrigger>;
      $[16] = noHover;
      $[17] = t5;
      $[18] = triggerContent;
      $[19] = t6;
    } else {
      t6 = $[19];
    }
    let t7;
    if ($[20] !== hoverCardContent) {
      t7 = <PopoverContent side="top" sideOffset={8} className="rounded-[20px] p-0" onClick={_temp4}>{hoverCardContent}</PopoverContent>;
      $[20] = hoverCardContent;
      $[21] = t7;
    } else {
      t7 = $[21];
    }
    let t8;
    if ($[22] !== isOpen || $[23] !== t6 || $[24] !== t7) {
      t8 = <div className="w-fit"><Popover open={isOpen} onOpenChange={setIsOpen}>{t6}{t7}</Popover></div>;
      $[22] = isOpen;
      $[23] = t6;
      $[24] = t7;
      $[25] = t8;
    } else {
      t8 = $[25];
    }
    return t8;
  }
  let t5;
  if ($[26] !== hoverCardContent || $[27] !== isOpen || $[28] !== noHover || $[29] !== triggerContent) {
    t5 = noHover ? triggerContent : <HoverCard open={isOpen} onOpenChange={setIsOpen} openDelay={200} closeDelay={100}><HoverCardTrigger asChild={true}>{triggerContent}</HoverCardTrigger><HoverCardContent style={{
        minWidth: "275px",
        maxWidth: "315px"
      }} side="top" sideOffset={8} onClick={_temp5}>{hoverCardContent}</HoverCardContent></HoverCard>;
    $[26] = hoverCardContent;
    $[27] = isOpen;
    $[28] = noHover;
    $[29] = triggerContent;
    $[30] = t5;
  } else {
    t5 = $[30];
  }
  let t6;
  if ($[31] !== t5) {
    t6 = <div className="w-fit">{t5}</div>;
    $[31] = t5;
    $[32] = t6;
  } else {
    t6 = $[32];
  }
  return t6;
};
export default IconRewardHover;
function _temp(e_2) {
  return e_2.stopPropagation();
}
function _temp2(value) {
  return !value;
}
function _temp3(prev) {
  return !prev;
}
function _temp4(e_0) {
  return e_0.stopPropagation();
}
function _temp5(e_1) {
  return e_1.stopPropagation();
}